import { useTranslation } from 'react-i18next';
import { FieldValues, useFormContext } from 'react-hook-form';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { useConfirmCancelForm } from '@cbridge-modules/unsaved-changes-confirmation';

import { FormProvider } from '../FormProvider/FormProvider';
import { useHasChanges } from '../useHasChanges/useHasChanges';
import type { IFormDialogProps } from './FormDialog.interface';

const FormDialogContent = <TFieldValues extends FieldValues = FieldValues,>(props: IFormDialogProps<TFieldValues>) => {
  const {
    open, onClose, onSubmit, title, isCreateMode, children,
  } = props;

  const { t } = useTranslation();

  const methods = useFormContext<TFieldValues>();

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid, isSubmitted, isDirty },
  } = methods;

  const hasChanges = useHasChanges();
  const { confirmCloseForm } = useConfirmCancelForm();
  const handleClose = async () => {
    if (!hasChanges || (await confirmCloseForm())) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog
      fullWidth={true}
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: handleSubmit((data) => onSubmit(data, methods)),
        },
      }}
    >
      <DialogTitle>
        {title}
      </DialogTitle>

      <DialogContent>
        {children}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color={'inherit'} disabled={isSubmitting}>
          {t('text:common.button.cancel')}
        </Button>

        <Button type={'submit'} loading={isSubmitting} disabled={isSubmitting || (isSubmitted && !isValid) || (!isCreateMode && !isDirty)}>
          {isCreateMode ? t('text:common.button.create') : t('text:common.button.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const FormDialog = <TFieldValues extends FieldValues = FieldValues,>(props: IFormDialogProps<TFieldValues>) => (
  <FormProvider
    schema={props.schema}
    values={props.values}
    enableFieldRevert={props.enableFieldRevert ?? !props.isCreateMode}
    mode={props.mode}
  >
    <FormDialogContent {...props} />
  </FormProvider>
);
